// The storefront's modal: a centred dialog, a side sheet (a bottom sheet on phones), or a wide "sheet" that is centred on
// screens and a bottom sheet on phones. It uses the same markup and
// behaviour as the dashboard's confirm dialog: focus moves to the heading, Tab stays inside, Escape or a click on the
// backdrop closes it, and focus returns to whatever opened it.
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../icons";

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

// Open dialogs, topmost last. Escape is heard on the document, because focus can sit outside the dialog (a button that
// locks while it saves drops focus to the page), and only the topmost dialog closes. The first one open locks the page's
// scroll and the last one closed gives it back, whatever order they go in.
const open: { current: () => void }[] = [];
let pageOverflow = "";
function closeTopmost(event: globalThis.KeyboardEvent) {
  if (event.key !== "Escape" || !open.length) return;
  event.preventDefault();
  open[open.length - 1].current();
}

// `above` is for a dialog opened from another dialog (the account picker over the item drawer), so it sits on top of it.
export function StoreDialog({ title, onClose, placement = "center", above = false, className = "", children }: { title: ReactNode; onClose: () => void; placement?: "center" | "side" | "sheet"; above?: boolean; className?: string; children: ReactNode }) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open.length) { document.addEventListener("keydown", closeTopmost); pageOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; }
    open.push(close);
    return () => {
      open.splice(open.indexOf(close), 1);
      if (!open.length) { document.removeEventListener("keydown", closeTopmost); document.body.style.overflow = pageOverflow; }
    };
  }, []);
  useEffect(() => {
    const opener = document.activeElement;
    heading.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Tab" || !panel.current) return;
    const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  // Mounted on the body: the page's <main> is its own stacking layer and would otherwise leave the footer painted over the sheet.
  return createPortal(<div className={`modal-backdrop store-backdrop ${placement} ${above ? "above" : ""}`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={panel} className={`dialog modal store-dialog ${placement} ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
      <header className="store-dialog-head">
        <h2 id={titleId} ref={heading} tabIndex={-1}>{title}</h2>
        <button type="button" className="icon-button ghost" aria-label="Close" onClick={onClose}><Icon name="close"/></button>
      </header>
      <div className="store-dialog-body">{children}</div>
    </div>
  </div>, document.body);
}
